import { supabase } from './supabase';

export interface AdminUser {
  id: string;
  username: string;
  full_name: string | null;
  bio: string | null;
  role: string;
  country: string;
  default_currency: string;
  created_at: string;
  updated_at: string;
}

export interface AdminImpersonation {
  id: string;
  admin_id: string;
  target_user_id: string;
  reason: string | null;
  admin_ip_address: string | null;
  admin_user_agent: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  status: 'active' | 'ended' | 'expired';
  created_at: string;
  admin: {
    username: string;
    full_name: string | null;
  };
  target_user: {
    username: string;
    full_name: string | null;
  };
}

export interface AdminFeatureFlag {
  id: string;
  flag_name: string;
  enabled: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface UsersResponse {
  users: AdminUser[];
  pagination: PaginationInfo;
}

export interface AuditLogsResponse {
  logs: AdminImpersonation[];
  pagination: PaginationInfo;
}

// Check if current user is admin
export async function isCurrentUserAdmin(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    return profile?.role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

// Get admin API headers
async function getAdminHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

// Fetch all users with pagination and search
export async function fetchUsers(params: {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
} = {}): Promise<UsersResponse> {
  const { page = 1, limit = 20, search = '', role = '' } = params;

  const headers = await getAdminHeaders();
  const searchParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(search && { search }),
    ...(role && { role }),
  });

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin/users?${searchParams}`,
    { headers }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch users');
  }

  return response.json();
}

// Start a temporary admin support session
export async function startImpersonation(params: {
  targetUserId: string;
  reason: string;
}): Promise<{
  success: boolean;
  impersonationId: string;
  supportToken: string;
  targetUser: { id: string; username: string };
  expiresAt: string;
  redirectUrl: string;
}> {
  const headers = await getAdminHeaders();

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin/impersonate`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to start impersonation');
  }

  return response.json();
}

// End a temporary admin support session
export async function endImpersonation(
  impersonationId: string,
  supportToken?: string,
  outcome: 'ended' | 'expired' = 'ended'
): Promise<void> {
  const headers = await getAdminHeaders();

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin/end-impersonation`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ impersonationId, supportToken, outcome }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to end impersonation');
  }
}

// Fetch audit logs
export async function fetchAuditLogs(params: {
  page?: number;
  limit?: number;
} = {}): Promise<AuditLogsResponse> {
  const { page = 1, limit = 50 } = params;

  const headers = await getAdminHeaders();
  const searchParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin/audit-logs?${searchParams}`,
    { headers }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch audit logs');
  }

  return response.json();
}

// Fetch feature flags
export async function fetchFeatureFlags(): Promise<{ flags: AdminFeatureFlag[] }> {
  const headers = await getAdminHeaders();

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin/feature-flags`,
    { headers }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch feature flags');
  }

  return response.json();
}

// Utility functions
export function formatDuration(seconds: number | null): string {
  if (!seconds) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
}

export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function getRoleColor(role: string): string {
  switch (role) {
    case 'admin':
      return 'bg-red-100 text-red-800';
    case 'creator':
      return 'bg-blue-100 text-blue-800';
    case 'user':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'ended':
      return 'bg-gray-100 text-gray-800';
    case 'expired':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}